import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react';
import { ROUTES } from '../../utils/routes';
import { useNavigate } from 'react-router-dom';
import { usePushEvents } from '../../hooks/PushEvents';
import { useEnabledFeatures } from '../../hooks/EnabledFeatures';
import { useDonorContext } from '../../providers/DonorProvider/DonorProvider';
import { getLoginTypeLocalStorage } from '../../providers/DonorProvider/donorLocalStorage';
import {
  lastLoginBannerDismiss,
  lastLoginBannerShown
} from '../../hooks/PushEvents/clevertapEvents';
import {
  DonorContextProps,
  DonorWithWallet
} from '../../providers/DonorProvider/interfaces';
import { requestInit } from '../../hooks/useRequestState';
import { ReactChildren } from '../../providers/DoneeProvider';

export const LastLoginContext = createContext<{
  show: boolean;
  forceHide: boolean;
  type: 'password' | 'facebook' | undefined;
  donor: DonorWithWallet | undefined;
  donorName: string | undefined;
  donorEmail: string | undefined;
  donorContext: DonorContextProps;
  handleLogin: () => unknown;
  handleCancel: () => unknown;
  handleFacebookClose: () => unknown;
  handleLoginWithPassword: () => unknown;
  setForceHide: React.Dispatch<React.SetStateAction<boolean>>;
}>({
  show: false,
  type: undefined,
  donor: undefined,
  forceHide: false,
  donorName: undefined,
  donorEmail: undefined,
  donorContext: {
    donor: requestInit(),
    login: requestInit(),
    totalGifts: 0,
    totalRecurringDonations: 0
  },
  setForceHide: () => null,
  handleCancel: () => null,
  handleLogin: () => null,
  handleLoginWithPassword: () => null,
  handleFacebookClose: () => null
});

export const useLastLogin = () => useContext(LastLoginContext);

let timeout: number = 0;
const LastLoginProvider: React.FC<ReactChildren> = ({ children }) => {
  const push = useNavigate();
  const [show, setShow] = useState<boolean>(false);
  const [forceHide, setForceHide] = useState<boolean>(false);
  const { isFeatureEnabled } = useEnabledFeatures();
  const { pushEvent } = usePushEvents();
  const { donorContext } = useDonorContext();
  const donor = donorContext.donor?.data || undefined;
  const loginType = getLoginTypeLocalStorage();
  const type = loginType?.loginType;
  const donorName = loginType?.donorName;
  const donorEmail = loginType?.donorEmail;

  useEffect(() => {
    window.clearTimeout(timeout);
    const shouldShow =
      Boolean(type) && isFeatureEnabled('giving_rememberlastlogin_oga');
    switch (donorContext.donor.type) {
      case 'REQUEST_INIT':
        timeout = window.setTimeout(() => {
          setShow(shouldShow);
          shouldShow && pushEvent(lastLoginBannerShown);
        }, 600);
        break;
      case 'REQUEST_ERROR':
        setShow(shouldShow);
        shouldShow && pushEvent(lastLoginBannerShown);
        break;
      case 'REQUEST_SUCCESS':
        setShow(false);
        break;
      case 'REQUEST_START':
      default:
        break;
    }
  }, [donorContext.donor.type, pushEvent, isFeatureEnabled]);

  const handleFacebookClose = useCallback(() => {
    setShow(false);
    pushEvent(lastLoginBannerDismiss, { reason: 'Facebook Button Clicked' });
  }, [setShow, pushEvent]);

  const handleCancel = useCallback(() => {
    pushEvent(lastLoginBannerDismiss, {
      reason: 'Cancel button clicked'
    });
  }, [pushEvent]);

  const handleLogin = useCallback(() => {
    setShow(false);
    pushEvent(lastLoginBannerDismiss, {
      reason: 'Sign in with another account button clicked'
    });
    push(ROUTES.login);
  }, [pushEvent]);
  const handleLoginWithPassword = useCallback(() => {
    setShow(false);
    pushEvent(lastLoginBannerDismiss, {
      reason: 'Sign in with password button clicked'
    });
    push(ROUTES.emailLogin(donorEmail));
  }, [setShow, pushEvent]);

  return (
    <LastLoginContext.Provider
      value={{
        show,
        type,
        donor,
        donorName,
        donorEmail,
        donorContext,
        forceHide,
        setForceHide,
        handleCancel,
        handleLogin,
        handleLoginWithPassword,
        handleFacebookClose
      }}
    >
      {children}
    </LastLoginContext.Provider>
  );
};

export default React.memo(LastLoginProvider);
